import { useEffect } from "react";

const SITE_URL = "https://teleportapp.sg";
const DEFAULT_IMAGE = `${SITE_URL}/images/hero-futuristic.jpeg`;

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

function upsertNamedLink(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertJsonLd(id, data) {
  let element = document.getElementById(id);
  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

export default function SEO({
  title,
  description,
  canonicalPath = "/",
  keywords,
  image = DEFAULT_IMAGE,
  imageAlt = "Teleport Now travel app",
  type = "website",
  robots = "index, follow, max-image-preview:large",
  alternates = [],
  jsonLd,
}) {
  useEffect(() => {
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    document.title = title;

    upsertMeta('meta[name="description"]', { name: "description", content: description });
    const keywordsMeta = document.head.querySelector('meta[name="keywords"]');
    if (keywords) {
      upsertMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
    } else {
      keywordsMeta?.remove();
    }
    upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    upsertMeta('meta[name="author"]', { name: "author", content: "Teleport Now" });
    upsertMeta('meta[name="application-name"]', {
      name: "application-name",
      content: "Teleport Now",
    });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    upsertMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: "Teleport Now",
    });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_SG" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
    upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: imageAlt });
    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:site"]', { name: "twitter:site", content: "@teleportnow" });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
    upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: imageAlt });
    upsertMeta('meta[name="apple-itunes-app"]', {
      name: "apple-itunes-app",
      content: `app-id=6756201717, app-argument=${canonicalUrl}`,
    });
    upsertMeta('meta[name="format-detection"]', {
      name: "format-detection",
      content: "telephone=no",
    });
    upsertLink("canonical", canonicalUrl);
    upsertNamedLink('link[rel="alternate"][hreflang="x-default"]', {
      rel: "alternate",
      hreflang: "x-default",
      href: canonicalUrl,
    });

    alternates.forEach(({ hreflang, href }) => {
      upsertNamedLink(`link[rel="alternate"][hreflang="${hreflang}"]`, {
        rel: "alternate",
        hreflang,
        href,
      });
    });

    if (jsonLd) {
      upsertJsonLd("page-json-ld", jsonLd);
    } else {
      document.getElementById("page-json-ld")?.remove();
    }
  }, [title, description, canonicalPath, keywords, image, imageAlt, type, robots, alternates, jsonLd]);

  return null;
}

export { SITE_URL, DEFAULT_IMAGE };
